import type { PixelEvent } from '@/events';

import type { BackpressureManager } from '../resilience/BackpressureManager';
import type { OrderingManager } from '../ordering/OrderingManager';
import type { IQueueStorage, QueuedEventRecord } from './storage/types';
import type { IEventQueue } from './types';

export class PersistentQueue implements IEventQueue {
  private readonly storage: IQueueStorage;

  private readonly ordering: OrderingManager;

  private readonly backpressure: BackpressureManager;

  private records: QueuedEventRecord[] = [];

  private loaded = false;

  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    storage: IQueueStorage,
    ordering: OrderingManager,
    backpressure: BackpressureManager
  ) {
    this.storage = storage;
    this.ordering = ordering;
    this.backpressure = backpressure;
  }

  async init(): Promise<void> {
    await this.storage.init();
    const loaded = await this.storage.load();
    this.records = this.ordering.sort(loaded);
    this.ordering.restoreFromRecords(this.records);
    this.loaded = true;
  }

  destroy(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    void this.flushPersist();
    this.storage.destroy();
    this.records = [];
    this.loaded = false;
  }

  enqueue(event: PixelEvent): void {
    this.ensureLoaded();

    const decision = this.backpressure.evaluateEnqueue(this.records.length);
    if (!decision.accepted) return;

    if (decision.policy === 'drop_oldest' && this.records.length > 0) {
      this.records.shift();
    }

    if (decision.policy === 'drop_newest' && decision.dropped > 0) {
      return;
    }

    const record = this.ordering.assign(event);
    const applied = this.backpressure.applyPolicy(this.records, record);
    this.records = applied.items;
    this.schedulePersist();
  }

  dequeue(): PixelEvent | undefined {
    this.ensureLoaded();
    const record = this.records.shift();
    this.schedulePersist();
    return record?.event;
  }

  peek(): PixelEvent | undefined {
    this.ensureLoaded();
    return this.records[0]?.event;
  }

  size(): number {
    this.ensureLoaded();
    return this.records.length;
  }

  clear(): void {
    this.records = [];
    this.schedulePersist();
  }

  remove(predicate: (event: PixelEvent) => boolean): number {
    this.ensureLoaded();
    const before = this.records.length;
    this.records = this.records.filter(record => !predicate(record.event));
    const removed = before - this.records.length;
    if (removed > 0) this.schedulePersist();
    return removed;
  }

  isEmpty(): boolean {
    return this.size() === 0;
  }

  getAll(): readonly PixelEvent[] {
    this.ensureLoaded();
    return this.records.map(record => record.event);
  }

  getRecords(): readonly QueuedEventRecord[] {
    this.ensureLoaded();
    return [...this.records];
  }

  importRecords(records: readonly QueuedEventRecord[]): void {
    this.records = this.ordering.sort(records);
    this.ordering.restoreFromRecords(this.records);
    this.schedulePersist();
  }

  exportRecords(): readonly QueuedEventRecord[] {
    return this.getRecords();
  }

  getStorageAdapterName(): string {
    return this.storage.getAdapterName();
  }

  async reload(): Promise<void> {
    const loaded = await this.storage.load();
    this.records = this.ordering.sort(loaded);
    this.ordering.restoreFromRecords(this.records);
    this.loaded = true;
  }

  private ensureLoaded(): void {
    if (!this.loaded) {
      this.loaded = true;
    }
  }

  private schedulePersist(): void {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      void this.flushPersist();
    }, 0);
  }

  private async flushPersist(): Promise<void> {
    await this.storage.save(this.records);
  }
}
